/**
 * Horaires hebdomadaires (expérience / établissement) — modèle UI ↔ contrat wire.
 *
 * Modèle UI (draft) : par jour `{ name, label, day, on, slots: [[start, end], …], nightFromPrev }`
 * en minutes depuis 00:00. La fin du DERNIER créneau peut dépasser 24:00 jusqu'à 05:00 le
 * lendemain (`end` ∈ ]1440, 1740]), avec ou sans pause sur ce jour. Le jour qui reçoit la nuit
 * ne peut pas avoir de pause propre (sa seule pause backend porte la nuit) : l'éditeur ne lui
 * propose pas de second créneau (« Pause indisponible », `pauseBlockedByNight`). Si un état en
 * masse (appliquer à tous, réactivation) lui en donne quand même deux, `normalizeWeek` coupe la
 * nuit de la veille à minuit.
 *
 * Contrat wire (backend, une ligne par jour, une seule pause) :
 * `{ enabled, close, periods: [{ start, end }], lunches: [{ start, end }] }` en "HH:MM" ≤ 23:59,
 * `end` strictement après `start` (Step4Request). Le backend n'a AUCUN flag « lendemain » :
 * un jour D qui ferme après minuit est stocké `D.end = 23:59` et `D+1.start = 00:00` avec
 * `D+1.lunch = [fin de nuit → vraie ouverture de D+1]` (TimeSlotService::getWorkPeriods,
 * AFTER_MIDNIGHT_LIMIT 05:00). Si D+1 n'a pas d'horaires propres : `00:00→23:59`,
 * `lunch [fin de nuit → 23:59]`, `is_open = 1`.
 *
 * Règle de conflit : c'est toujours la nuit du jour précédent qui est tronquée, jamais les
 * horaires propres du jour suivant qui sont réécrits.
 */
export const DAY_MINUTES = 1440
// Dernier instant stockable sur la journée : 23:59.
export const MAX_DAY_MINUTES = DAY_MINUTES - 1 // 1439
// Fin maximale du lendemain pour un créneau qui chevauche minuit : 05:00.
export const OVERNIGHT_MAX_END = 300
export const OVERNIGHT_MAX_MINUTES = DAY_MINUTES + OVERNIGHT_MAX_END // 1740
export const MIN_SLOT_MINUTES = 30
export const MIN_PAUSE_MINUTES = 30
export const GRID_STEP = 15
// 09:00 → 23:45 : créneau proposé quand un jour est (ré)ouvert sans horaire valide.
export const DEFAULT_SLOT = [540, 1425]
export const DAY_NUMBER_BY_NAME = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 0,
}

const END_OF_DAY_HHMM = '23:59'

function isNum(value) {
    return typeof value === 'number' && !Number.isNaN(value)
}

/**
 * True when the slot closes after midnight (end > 24:00).
 * e.g. [1080, 1560] (18:00 → 02:00 le lendemain).
 */
export function isOvernightSlot(slot) {
    if (!slot || !isNum(slot[0]) || !isNum(slot[1])) return false
    return slot[1] > DAY_MINUTES
}

/**
 * Open duration of a single slot in minutes.
 */
export function slotDuration(slot) {
    if (!slot || !isNum(slot[0]) || !isNum(slot[1])) return 0
    return Math.max(0, slot[1] - slot[0])
}

/**
 * Convert a "HH:MM" (optionally "HH:MM:SS") string into minutes from 00:00.
 * Tolerant of legacy values > 24:00 (up to 29:59).
 * Returns null when the value cannot be parsed or is out of range.
 */
export function hhmmToMinutes(time) {
    if (typeof time !== 'string') return null
    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim())
    if (!match) return null
    const hours = parseInt(match[1], 10)
    const minutes = parseInt(match[2], 10)
    if (hours > 29 || minutes > 59) return null
    return hours * 60 + minutes
}

/**
 * Convert minutes from 00:00 into a zero-padded "HH:MM" string (e.g. 1500 → "25:00").
 */
export function minutesToHHMM(minutes) {
    if (minutes === null || minutes === undefined || minutes < 0 || Number.isNaN(minutes)) return ''
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

/**
 * Wire representation of a minute value: 24:00 (and anything beyond) is stored as "23:59".
 */
function minutesToWire(minutes) {
    if (minutes >= DAY_MINUTES) return END_OF_DAY_HHMM
    return minutesToHHMM(minutes)
}

export function snapToGrid(minutes, step = GRID_STEP) {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return null
    return Math.round(minutes / step) * step
}

export function isOnGrid(minutes, step = GRID_STEP) {
    if (minutes === null || minutes === undefined) return false
    return minutes % step === 0
}

/**
 * Human readable time: values ≥ 24:00 are folded and suffixed with the next-day label.
 * formatTime(1500, 'le lendemain') → "01:00 (le lendemain)".
 */
export function formatTime(minutes, nextDayLabel) {
    if (!isNum(minutes)) return ''
    // 1440 = fermeture à minuit = « 23:59 » sur le wire : affiché tel quel, sans « lendemain ».
    if (minutes === DAY_MINUTES) return END_OF_DAY_HHMM
    if (minutes > DAY_MINUTES) {
        const base = minutesToHHMM(minutes - DAY_MINUTES)
        return nextDayLabel ? `${base} (${nextDayLabel})` : base
    }
    return minutesToHHMM(minutes)
}

export function formatSlotLabel(slot, nextDayLabel, sep = ' – ') {
    if (!slot) return ''
    return `${formatTime(slot[0], nextDayLabel)}${sep}${formatTime(slot[1], nextDayLabel)}`
}

/**
 * Résumé d’ouverture sans libellé de nuit ni suffixe « (Lendemain) ».
 * Ex. [[540, 780], [840, 1695]] → "09:00–13:00 · 14:00–04:15".
 */
export function formatSummarySlots(slots) {
    if (!Array.isArray(slots) || slots.length === 0) return ''
    return slots.map((slot) => formatSlotLabel(slot, '', '–')).join(' · ')
}

/**
 * Human readable duration, e.g. 90 → « 1 h 30 », 480 → « 8 h ».
 */
export function minutesReadable(minutes) {
    const total = Math.max(0, Math.round(minutes || 0))
    const hours = Math.floor(total / 60)
    const mins = total % 60
    return mins ? `${hours} h ${String(mins).padStart(2, '0')}` : `${hours} h`
}

/**
 * Sum of the open durations (minutes) of all slots.
 */
export function slotsDuration(slots) {
    if (!Array.isArray(slots)) return 0
    return slots.reduce((sum, slot) => sum + slotDuration(slot), 0)
}

/**
 * Minutes after midnight at which the day's night ends (0 < n ≤ 300), or null when the day
 * does not run past midnight. Only an OPEN day can be overnight (via its last slot).
 */
export function nightEndOf(day) {
    if (!day || day.on !== true) return null
    const end = lastSlotEnd(day)
    if (end === null || end <= DAY_MINUTES) return null
    return Math.min(end, OVERNIGHT_MAX_MINUTES) - DAY_MINUTES
}

function lastSlotEnd(day) {
    if (!day || !Array.isArray(day.slots) || !day.slots.length) return null
    const end = day.slots[day.slots.length - 1][1]
    return isNum(end) ? end : null
}

function resolveDayNumber(day) {
    if (day && typeof day.day === 'number') return day.day
    const name = day && typeof day.name === 'string' ? day.name.toLowerCase() : ''
    return Object.prototype.hasOwnProperty.call(DAY_NUMBER_BY_NAME, name) ? DAY_NUMBER_BY_NAME[name] : 0
}

/* ------------------------------------------------------------------ */
/* Wire → UI                                                           */
/* ------------------------------------------------------------------ */

/**
 * Parse ONE wire day into its own slots plus a possible "night tail"
 * ([00:00 → tail] belonging to the previous day). Pure, no cross-day knowledge.
 */
function parseWireDay(day) {
    const enabled = !!day && day.enabled !== false && day.close !== true
    const period = day && Array.isArray(day.periods) ? day.periods[0] : null
    const result = { enabled, wireStart: null, wireEnd: null, tail: null, slots: [] }
    if (!period) return result

    let start = hhmmToMinutes(period.start)
    let end = hhmmToMinutes(period.end)
    if (start === null) start = 0
    if (end === null) end = 0
    start = Math.min(Math.max(start, 0), MAX_DAY_MINUTES)
    if (end === MAX_DAY_MINUTES || end === DAY_MINUTES) end = DAY_MINUTES
    if (end > DAY_MINUTES) end = Math.min(end, OVERNIGHT_MAX_MINUTES)
    result.wireStart = start
    result.wireEnd = end

    const lunch = day && Array.isArray(day.lunches) ? day.lunches[0] : null
    let ls = lunch ? hhmmToMinutes(lunch.start) : null
    let le = lunch ? hhmmToMinutes(lunch.end) : null
    if (ls === MAX_DAY_MINUTES) ls = DAY_MINUTES
    if (le === MAX_DAY_MINUTES) le = DAY_MINUTES
    // Pause « vide » (00:00/00:00, ou X→X comme 23:59/23:59, 21:00/21:00) = aucune pause.
    const hasLunch = ls !== null && le !== null && !(ls === 0 && le === 0) && ls !== le

    let slots = []
    if (start === 0 && hasLunch && ls > 0 && ls <= OVERNIGHT_MAX_END) {
        // Queue de nuit du jour précédent (00:00 → ls), puis horaires propres à partir de le.
        result.tail = ls
        if (le < end) slots.push([le, end])
    } else if (start === 0 && !hasLunch && end > 0 && end <= OVERNIGHT_MAX_END) {
        // Nuit seule, sans horaires propres (forme legacy « 00:00 → 01:45 »).
        result.tail = end
    } else if (hasLunch) {
        if (ls > start && ls < end) slots.push([start, Math.min(ls, DAY_MINUTES)])
        if (le < end && le >= start) slots.push([le, end])
        if (!slots.length) slots.push([start, end])
    } else {
        slots.push([start, end])
    }

    slots = slots.filter((slot) => slot[1] > slot[0])
    if (slots.length > 1) {
        const last = slots[slots.length - 1]
        last[1] = Math.min(last[1], DAY_MINUTES)
    }
    // Le contenu d'un jour fermé est du bruit côté backend (mis à zéro avant lecture).
    result.slots = enabled ? slots : []
    return result
}

/**
 * Deserialize the 7 wire days into the editable week model.
 * Cross-day pass (needs exactly 7 days, Monday first, Sunday → Monday wraps): when day D ends
 * at 23:59 and day D+1 starts at 00:00 with a night tail, the tail is re-attached to D's single
 * slot (`D.end = 1440 + tail`). Tails that cannot be attached are dropped (one-way upgrade).
 */
export function parseWeek(days) {
    const list = (days || []).map((day) => ({ day, parsed: parseWireDay(day) }))
    const n = list.length
    const nights = new Array(n).fill(null)

    if (n === 7) {
        for (let i = 0; i < n; i++) {
            const cur = list[i].parsed
            const prev = list[(i + n - 1) % n].parsed
            if (cur.tail === null || cur.wireStart !== 0) continue
            if (!prev.enabled || prev.wireEnd !== DAY_MINUTES || !prev.slots.length) continue
            prev.slots[prev.slots.length - 1][1] = DAY_MINUTES + cur.tail
            nights[i] = cur.tail
        }
    }

    return list.map(({ day, parsed }, i) => ({
        name: day && day.name ? day.name : '',
        label: day && day.label ? day.label : '',
        day: resolveDayNumber(day),
        on: parsed.enabled && parsed.slots.length > 0,
        slots: parsed.slots.map((slot) => [slot[0], slot[1]]),
        nightFromPrev: nights[i],
    }))
}

/* ------------------------------------------------------------------ */
/* UI → Wire                                                           */
/* ------------------------------------------------------------------ */

function sortedValidSlots(slots) {
    return (slots || [])
        .filter((slot) => slot && isNum(slot[0]) && isNum(slot[1]))
        .map((slot) => [slot[0], slot[1]])
        .sort((a, b) => a[0] - b[0])
}

/**
 * Serialize the week model back into the wire contract (see file header).
 */
export function serializeWeek(draftDays) {
    const list = draftDays || []
    const n = list.length
    return list.map((d, i) => {
        const prev = n === 7 ? list[(i + n - 1) % n] : null
        let night = prev ? nightEndOf(prev) : null
        let own = d && d.on === true ? sortedValidSlots(d.slots).filter((slot) => slot[1] > slot[0]) : []

        const base = {
            name: d && d.name ? d.name : '',
            label: d && d.label ? d.label : '',
            day: resolveDayNumber(d),
        }

        if (night !== null && own.length > 1) {
            // Garde : une nuit reçue interdit une pause propre (une seule pause par jour).
            own = [[own[0][0], own[own.length - 1][1]]]
        }
        if (night !== null && own.length === 1 && own[0][0] <= night) {
            // Garde : la nuit doit se terminer avant l'ouverture propre, sinon on l'ignore.
            night = null
        }

        if (night !== null) {
            const hasOwn = own.length === 1
            return {
                ...base,
                enabled: true,
                close: false,
                periods: [{ start: '00:00', end: hasOwn ? minutesToWire(own[0][1]) : END_OF_DAY_HHMM }],
                lunches: [{ start: minutesToHHMM(night), end: hasOwn ? minutesToWire(own[0][0]) : END_OF_DAY_HHMM }],
            }
        }

        if (!own.length) {
            return { ...base, enabled: false, close: true, periods: [], lunches: [] }
        }

        if (own.length === 1) {
            return {
                ...base,
                enabled: true,
                close: false,
                periods: [{ start: minutesToWire(own[0][0]), end: minutesToWire(own[0][1]) }],
                lunches: [],
            }
        }

        const first = own[0]
        const last = own[own.length - 1]
        return {
            ...base,
            enabled: true,
            close: false,
            periods: [{ start: minutesToWire(first[0]), end: minutesToWire(last[1]) }],
            lunches: last[0] > first[1] ? [{ start: minutesToWire(first[1]), end: minutesToWire(last[0]) }] : [],
        }
    })
}

/* ------------------------------------------------------------------ */
/* Bounds & normalisation                                              */
/* ------------------------------------------------------------------ */

/**
 * Highest end allowed for the single slot of day `di`, taking the next day into account
 * (the next day's own opening must leave a 30-min pause after the night).
 */
function overnightMax(draftDays, di) {
    const n = draftDays.length
    if (n !== 7) return DAY_MINUTES
    const next = draftDays[(di + 1) % n]
    if (!next || next.on !== true || !Array.isArray(next.slots) || !next.slots.length) return OVERNIGHT_MAX_MINUTES
    if (next.slots.length !== 1) return DAY_MINUTES
    const nextStart = next.slots[0][0]
    if (!isNum(nextStart)) return DAY_MINUTES
    return Math.min(OVERNIGHT_MAX_MINUTES, Math.max(DAY_MINUTES, DAY_MINUTES + nextStart - MIN_PAUSE_MINUTES))
}

/**
 * Valid bounds (minutes) for one field of one slot of one day, cross-day aware.
 */
export function weekSlotBounds(draftDays, di, si, isEnd) {
    const list = draftDays || []
    const n = list.length
    const d = list[di] || { slots: [] }
    const slots = Array.isArray(d.slots) ? d.slots : []
    const current = slots[si] || [0, 0]

    if (isEnd) {
        const min = current[0] + MIN_SLOT_MINUTES
        let max
        if (si < slots.length - 1) {
            max = slots[si + 1][0] - MIN_PAUSE_MINUTES
        } else {
            max = overnightMax(list, di)
        }
        return { min, max: Math.max(min, max) }
    }

    let min = 0
    if (si > 0) {
        min = slots[si - 1][1] + MIN_PAUSE_MINUTES
    } else if (n === 7) {
        const prev = list[(di + n - 1) % n]
        const night = nightEndOf(prev)
        if (night !== null) {
            min = night + MIN_PAUSE_MINUTES
        } else if (prev && prev.on === true && lastSlotEnd(prev) >= DAY_MINUTES && slots.length > 1) {
            // Le moteur lirait lunch_start comme fin de nuit : un jour à pause ne peut pas ouvrir à 00:00.
            min = MIN_PAUSE_MINUTES
        }
    }
    const max = Math.min(current[1] - MIN_SLOT_MINUTES, MAX_DAY_MINUTES)
    return { min, max: Math.max(min, max) }
}

/**
 * True when the single slot leaves room for a second one. splitSlot() cuts at the middle and
 * keeps MIN_PAUSE on EACH side of it, so both halves reach MIN_SLOT only from 2 × 30 + 2 × 30
 * = 2 h (below that they would be dropped by the normalisation). A slot running past midnight
 * can be split too. Day-level rule (night received from the previous day): canSplitDay().
 */
export function canSplitSlot(slots) {
    if (!Array.isArray(slots) || slots.length !== 1) return false
    const [start, end] = slots[0]
    if (!isNum(start) || !isNum(end)) return false
    return end - start >= MIN_SLOT_MINUTES * 2 + MIN_PAUSE_MINUTES * 2
}

/**
 * True when day `di` receives the previous day's night (7-day week, Sunday → Monday wraps).
 */
export function receivesNight(draftDays, di) {
    const list = draftDays || []
    if (list.length !== 7) return false
    return nightEndOf(list[(di + 6) % 7]) !== null
}

/**
 * True when the open day `di` has a single slot whose only backend pause is already taken by
 * the previous day's night: the editor shows « Pause indisponible » instead of « + ».
 */
export function pauseBlockedByNight(draftDays, di) {
    const d = (draftDays || [])[di]
    if (!d || d.on !== true || !Array.isArray(d.slots) || d.slots.length !== 1) return false
    return receivesNight(draftDays, di)
}

/**
 * True when the editor may offer a second slot on day `di`: open, one slot of at least 2 h,
 * and no night received from the previous day (its single pause would be taken twice).
 */
export function canSplitDay(draftDays, di) {
    const d = (draftDays || [])[di]
    if (!d || d.on !== true) return false
    return canSplitSlot(d.slots) && !receivesNight(draftDays, di)
}

/**
 * Split a unique slot into two slots around its middle, preserving the mandatory pause.
 * Returns a NEW array.
 */
export function splitSlot(slots) {
    const [open, close] = (slots && slots[0]) || [0, 0]
    const middle = Math.round((open + close) / 2 / GRID_STEP) * GRID_STEP
    return [
        [open, middle - MIN_PAUSE_MINUTES],
        [middle + MIN_PAUSE_MINUTES, close],
    ]
}

/**
 * Merge slots back into a single slot spanning from the first start to the last end.
 */
export function mergeSlots(slots) {
    if (!Array.isArray(slots) || !slots.length) return [[0, 0]]
    return [[slots[0][0], slots[slots.length - 1][1]]]
}

function sanitizeDaySlots(day) {
    // Un créneau < 30 min est du bruit (wire fermé « 00:00/00:00 », état périmé) : ignoré.
    let slots = sortedValidSlots(day && day.slots).filter((slot) => slot[1] - slot[0] >= MIN_SLOT_MINUTES)
    const lastIndex = slots.length - 1

    slots = slots.map((slot, i) => {
        let start = Math.round(slot[0])
        let end = Math.round(slot[1])
        start = Math.min(Math.max(start, 0), MAX_DAY_MINUTES)
        if (i > 0) start = Math.min(Math.max(start, slots[i - 1][1] + MIN_PAUSE_MINUTES), MAX_DAY_MINUTES)
        end = Math.max(end, start + MIN_SLOT_MINUTES)
        // Seul le dernier créneau peut passer minuit ; les autres restent sur la journée.
        end = Math.min(end, i === lastIndex ? OVERNIGHT_MAX_MINUTES : MAX_DAY_MINUTES)
        slots[i] = [start, end]
        return slots[i]
    })

    slots = slots.filter((slot) => slot[1] - slot[0] >= MIN_SLOT_MINUTES)
    if (day && day.on === true && !slots.length) slots = [[DEFAULT_SLOT[0], DEFAULT_SLOT[1]]]
    return slots
}

/**
 * Return a NEW, coherent week: per-day sanitation (ordering, minimum durations, caps) then the
 * cross-day rule « the previous day's night is truncated » (single pass, only ends move).
 */
export function normalizeWeek(draftDays) {
    const out = (draftDays || []).map((d) => ({
        name: d && d.name ? d.name : '',
        label: d && d.label ? d.label : '',
        day: resolveDayNumber(d),
        on: !!d && d.on === true,
        slots: sanitizeDaySlots(d),
        nightFromPrev: null,
    }))
    const n = out.length
    if (n !== 7) return out

    for (let i = 0; i < n; i++) {
        const d = out[i]
        if (nightEndOf(d) === null) continue
        const max = overnightMax(out, i)
        const last = d.slots[d.slots.length - 1]
        if (last[1] > max) last[1] = max
    }

    for (let i = 0; i < n; i++) {
        const d = out[i]
        const prev = out[(i + n - 1) % n]
        if (!d.on || d.slots.length < 2) continue
        if (!prev.on || lastSlotEnd(prev) < DAY_MINUTES) continue
        if (d.slots[0][0] < MIN_PAUSE_MINUTES) {
            d.slots[0][0] = MIN_PAUSE_MINUTES
            d.slots[0][1] = Math.max(d.slots[0][1], MIN_PAUSE_MINUTES + MIN_SLOT_MINUTES)
        }
    }

    for (let i = 0; i < n; i++) {
        out[i].nightFromPrev = nightEndOf(out[(i + n - 1) % n])
    }
    return out
}
